import { ReactNode } from 'react';
import type {
  GetStaticPropsContext,
  InferGetStaticPropsType,
  GetStaticPathsResult,
  GetStaticPropsResult,
} from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import cn from 'classnames';
import { PrismicDocument } from '@prismicio/client';
import {
  getBlogHomePage,
  getAllBlogCategories,
  getBlogPostsByPage,
  getBlogPostsByCategory,
} from '@adapters/prismic/fetching/blog';
import Container from '@components/global/Container';
import Layout from '@components/global/Layout';
import CardLink from '@components/cms/CardLink';
import CardLinkGrid from '@components/cms/CardLinkGrid';
import HeroImage from '@components/cms/Slices/HeroImage';
import linkResolver from '@adapters/prismic/utils/linkResolver';
import useLink from '@hooks/useLink';

import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';

import { PageType } from '@adapters/prismic/types/slices';
import { altLocale } from '@utils/locale';
import { localeAltPath, localeAltPathAbs, localePathAbs } from '@utils/url';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';

import styles from './BlogHome.module.scss';
import { FEATURE_FLAG_PROMO_BAR_SERVER_SIDE } from '@config/feature-flag';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';

const pageSize = 8;

interface BlogHomePageProps {
  blogHomePage: any;
  blogFilters: PrismicDocument[];
  blogs: PrismicDocument[];
  totalPageCount: number;
  category: string;
  currentPage: number;
  alternateLocaleUrl?: string;
  canonicalUrl: string;
  langAltUrl: string;
  locale: string | undefined;
  promoMessages?: PromoTicker[];
  stores: any;
  xDefault: string;
}

export const getStaticProps = async ({
  params,
  previewData,
  preview = false,
}: GetStaticPropsContext<{ locale: string; slug: string }>): Promise<
  GetStaticPropsResult<BlogHomePageProps>
> => {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  const defaultLocale = 'en';
  if (!params?.slug) {
    throw new Error('Blog post has no slug');
  }

  if (!locale) {
    throw new Error('No locale specified for page');
  }

  const filter: string = params.slug[0];
  const page: number =
    params.slug.length > 1 ? +params.slug[params.slug.length - 1] : 1;

  const previewRef = getPreviewRef(preview, previewData);

  const [
    blogHomePage,
    categories,
    blogPostsResult,
    stores,
    promoMessages,
    bannerStripData,
    serverSideTranslation,
    promoBarData,
  ] = await Promise.allSettled([
    getBlogHomePage(locale ?? defaultLocale, previewRef),
    getAllBlogCategories(locale ?? defaultLocale, previewRef),
    getBlogPostsByPage(locale, pageSize, page, previewRef),
    getAllStoresDataForSetMyStore(locale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      locale &&
      getAllPromoTickerData(locale),
    getBannerStripGlobalData(locale, preview ? previewRef : undefined),
    serverSideTranslations(locale, ['common', 'promotions']),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  // add All/Tout as a category filter
  const allCategory = {
    ...categories?.[0],
    uid: 'all',
    data: { ...categories?.[0].data, name: locale === 'en' ? 'All' : 'Tout' },
  };

  const blogFilters = [allCategory, ...categories];

  let blogPosts;
  if (filter === 'all') {
    blogPosts = blogPostsResult;
  } else {
    const category = blogFilters.filter(
      (blogFilter) => blogFilter.uid === filter
    );

    blogPosts = await getBlogPostsByCategory(
      category[0]?.id,
      locale,
      page,
      pageSize,
      previewRef
    );
  }

  const pagePath = locale === 'en' ? 'blog' : 'blogue';
  const pageAltPath = locale === 'en' ? 'blogue' : 'blog';
  const pageSlugs = Array.isArray(params.slug) ? params.slug : [params.slug];
  const canonicalUrl = localePathAbs(locale, pagePath, ...pageSlugs);
  const alternateLocaleUrl = localeAltPath(locale, pageAltPath, ...pageSlugs);
  const langAltUrl = localeAltPathAbs(locale, pageAltPath, ...pageSlugs);
  const xDefault = locale === 'en' ? canonicalUrl : langAltUrl;

  return {
    props: {
      locale,
      blogHomePage,
      blogFilters,
      blogs: blogPosts?.results ?? [],
      totalPageCount: blogPosts?.total_pages ?? 0,
      category: filter || 'all',
      currentPage: page,
      stores,
      canonicalUrl,
      langAltUrl,
      alternateLocaleUrl,
      xDefault,
      ...serverSideTranslation,
      promoMessages,
      promoBarData,
      bannerStripData,
    },
  };
};

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export default function BlogPostHome({
  blogHomePage,
  blogFilters,
  blogs,
  totalPageCount,
  category,
  currentPage,
  locale,
  canonicalUrl,
  langAltUrl,
  xDefault,
}: InferGetStaticPropsType<typeof getStaticProps>): ReactNode {
  const description = blogHomePage?.data?.meta_description ?? '404';
  const title = blogHomePage?.data?.meta_title ?? '404';
  const image = blogHomePage?.data?.image ?? null;
  const heading = blogHomePage?.data?.heading ?? '404';
  const resolveLink = useLink();
  const pagination = [];
  const pagesToShow = 8;
  const pageMidPoint = Math.ceil(pagesToShow / 2);
  const minPage =
    currentPage - pageMidPoint < 1 ? 1 : currentPage - pageMidPoint;
  const maxPage =
    currentPage + pageMidPoint > totalPageCount
      ? totalPageCount
      : currentPage + pageMidPoint;

  if (currentPage > 1) {
    pagination.push({
      url: currentPage - 1,
      label: '<',
      showMobile: true,
      showSm: true,
    });
  }

  if (minPage > 1) {
    pagination.push({ url: '1', label: '1', showMobile: false, showSm: true });
    if (minPage - 1 > 1) {
      pagination.push({
        url: null,
        label: '...',
        showMobile: true,
        showSm: true,
      });
    }
  }

  for (let i = minPage; i <= maxPage; i++) {
    pagination.push({
      url: i,
      label: i,
      showMobile: [i, i + 1, i - 1].includes(currentPage),
      showSm: [i, i + 1, i - 1].includes(currentPage),
    });
  }

  if (maxPage < totalPageCount) {
    if (maxPage + 1 < totalPageCount) {
      pagination.push({
        url: null,
        label: '...',
        showSm: true,
        showMobile: true,
      });
    }
    pagination.push({
      url: totalPageCount,
      label: totalPageCount,
      showMobile: false,
      showSm: true,
    });
  }

  if (currentPage < maxPage) {
    pagination.push({
      url: currentPage + 1,
      label: '>',
      showMobile: true,
      showSm: true,
    });
  }

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonicalUrl}
        languageAlternates={[
          { hrefLang: altLocale(locale), href: langAltUrl },
          { hrefLang: String(locale), href: xDefault },
          { hrefLang: 'x-default', href: xDefault },
        ]}
        openGraph={{
          title,
          description,
          images: [
            {
              url: image.url,
              width: image.dimensions.width,
              height: image.dimensions.height,
              alt: image.alt,
            },
          ],
        }}
      />
      <HeroImage image={image} heading={heading} pageType={PageType.blogPage} />

      <>
        <Container>
          <nav className={styles.filters} id="blog-nav">
            <ul className={styles.filtersList}>
              {blogFilters.map((blogFilter: PrismicDocument) => (
                <li
                  key={`blog-filter-${blogFilter.id}-${blogFilter.uid}`}
                  className={cn(styles.filtersItem, {
                    [styles.filtersItemCurrent]: blogFilter.uid === category,
                  })}
                >
                  {blogFilter.uid === category ? (
                    blogFilter.data.name
                  ) : (
                    <a
                      className={styles.filtersItemLink}
                      href={linkResolver(blogFilter)}
                    >
                      {blogFilter.data.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </Container>
        <Container className={styles.articlesSectionContainer}>
          <div className={styles.blogPostsContainer}>
            <CardLinkGrid classNames={styles.blogPostsLinkGrid}>
              {blogs?.map((blogPost: PrismicDocument) => (
                <CardLink
                  key={`blog-link-${blogPost.id}`}
                  link={linkResolver(blogPost)}
                  imageUrl={blogPost.data.hero_image?.url}
                  imageAlt={blogPost.data.hero_image?.alt}
                  date={blogPost.data.formattedDate}
                  title={blogPost.data.title}
                  excerpt={blogPost.data.excerpt}
                />
              ))}
            </CardLinkGrid>

            <nav className="pagination" aria-label="Pagination">
              <ul className={styles.paginationControl}>
                {pagination.map((page) => (
                  <li
                    key={`page-${page.label}`}
                    className={cn(styles.paginationItem, {
                      [styles.paginationItemActive]: page.label === currentPage,
                      [styles.paginationItemMobile]: page.showMobile,
                      [styles.paginationItemSm]: page.showSm,
                    })}
                  >
                    {page.url !== null && +page.label !== currentPage ? (
                      <a
                        href={resolveLink(
                          'blogHome',
                          `${category}/page/${page.url}`
                        )}
                        className={styles.paginationLink}
                      >
                        {page.label}
                      </a>
                    ) : (
                      `${page.label}`
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </Container>
      </>
    </>
  );
}

BlogPostHome.Layout = Layout;
